import type {
	ColumnSizingState,
	ColumnVisibilityState,
	RowData,
} from "@tanstack/react-table";
import type { RefObject } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AppColumnDef } from "@/components/Molecules/Table/useDataTable";

/** Default minimum column width when not specified in column definition */
const DEFAULT_MIN_SIZE = 150;

/** Column id that grows into spare space (capped) before the last column flexes */
const DESCRIPTION_COLUMN_ID = "description";

/** Maximum width for the description column before spare space goes to the last column */
const DESCRIPTION_COLUMN_MAX_SIZE = 550;

/** Absolute minimum width for flex columns */
const MIN_FLEX_COLUMN_WIDTH = 60;

export interface UseTableColumnSizingOptions<TData extends RowData> {
	/** Column definitions from TanStack Table */
	columns: AppColumnDef<TData>[];
	/** Ref to the table container element for measuring width */
	containerRef: RefObject<HTMLElement | null>;
	/** Current column visibility state */
	columnVisibility?: ColumnVisibilityState;
	/** Current column order (array of column IDs) */
	columnOrder?: string[];
	/** Whether column resizing is enabled. When false, the hook returns empty state and no-op handlers */
	enabled?: boolean;
}

export interface UseTableColumnSizingReturn {
	/** Calculated column sizing state to pass to TanStack Table */
	columnSizing: ColumnSizingState;
	/** Whether the initial sizing has been calculated */
	isInitialized: boolean;
	/** Handler for column sizing changes that adjusts flex columns */
	handleColumnSizingChange: (
		updater:
			| ColumnSizingState
			| ((prev: ColumnSizingState) => ColumnSizingState),
	) => void;
	/** Clear manual/flex sizes and recompute from container width (Manage Columns Reset). */
	resetColumnSizing: () => void;
	/** The ID of the last visible column */
	lastVisibleColumnId: string | null;
	/** Primary capped flex column (description when visible, else last column) */
	flexColumnId: string | null;
}

// No-op handler for when resizing is disabled
const noopColumnSizingChange = () => {};

function getColumnDefaultSize<TData extends RowData>(
	col: AppColumnDef<TData>,
): number {
	return col.minSize ?? col.size ?? DEFAULT_MIN_SIZE;
}

function clamp(value: number, min: number, max: number): number {
	return Math.min(max, Math.max(min, value));
}

function getDescriptionMaxSize<TData extends RowData>(
	columns: AppColumnDef<TData>[],
): number {
	const descriptionColumn = columns.find(
		(col) => col.id === DESCRIPTION_COLUMN_ID,
	);
	return descriptionColumn?.maxSize ?? DESCRIPTION_COLUMN_MAX_SIZE;
}

/**
 * Hook to calculate and manage table column widths based on container size.
 */
export function useTableColumnSizing<TData extends RowData>({
	columns,
	containerRef,
	columnVisibility = {},
	columnOrder,
	enabled = true,
}: UseTableColumnSizingOptions<TData>): UseTableColumnSizingReturn {
	const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
	const [isInitialized, setIsInitialized] = useState(false);
	const [containerWidth, setContainerWidth] = useState(0);

	const descriptionMaxSize = useMemo(
		() => getDescriptionMaxSize(columns),
		[columns],
	);

	// Early return with empty state when disabled
	// Note: All hooks must still be called above to maintain consistent hook order
	const disabledReturn = useMemo(
		() => ({
			columnSizing: {} as ColumnSizingState,
			isInitialized: true, // Return true so tables don't wait for initialization
			handleColumnSizingChange: noopColumnSizingChange,
			resetColumnSizing: noopColumnSizingChange,
			lastVisibleColumnId: null,
			flexColumnId: null,
		}),
		[],
	);

	// Get visible columns in order
	const visibleColumnsInOrder = useMemo(() => {
		const allColumnIds = columns.map((col) => col.id as string);
		const orderedIds = columnOrder?.length
			? columnOrder.filter((id) => allColumnIds.includes(id))
			: allColumnIds;

		return orderedIds.filter((id) => {
			if (id in columnVisibility) {
				return columnVisibility[id];
			}
			return true;
		});
	}, [columns, columnOrder, columnVisibility]);

	// Get the last visible column ID
	const lastVisibleColumnId = useMemo(() => {
		return visibleColumnsInOrder.length > 0
			? visibleColumnsInOrder[visibleColumnsInOrder.length - 1]
			: null;
	}, [visibleColumnsInOrder]);

	const hasVisibleDescription = visibleColumnsInOrder.includes(
		DESCRIPTION_COLUMN_ID,
	);

	const descriptionIsLast =
		hasVisibleDescription && lastVisibleColumnId === DESCRIPTION_COLUMN_ID;

	/** Description + last column share spare space (description capped) */
	const hasCappedDescriptionWithLastFlex =
		hasVisibleDescription && lastVisibleColumnId != null && !descriptionIsLast;

	const flexColumnId = useMemo(() => {
		if (hasVisibleDescription) {
			return DESCRIPTION_COLUMN_ID;
		}
		return lastVisibleColumnId;
	}, [hasVisibleDescription, lastVisibleColumnId]);

	// Track layout changes that require resetting previously flexed columns
	const previousLayoutKey = useRef<string | null>(null);

	// Create a map of column ID to default/min width
	const columnMinSizes = useMemo(() => {
		const sizes: Record<string, number> = {};
		for (const col of columns) {
			const id = col.id as string;
			sizes[id] = getColumnDefaultSize(col);
		}
		return sizes;
	}, [columns]);

	const calculateColumnSizes = useCallback(
		(
			width: number,
			existingSizing: ColumnSizingState = {},
		): ColumnSizingState => {
			if (width <= 0 || visibleColumnsInOrder.length === 0) {
				return existingSizing;
			}

			if (hasCappedDescriptionWithLastFlex && lastVisibleColumnId) {
				const sizing: ColumnSizingState = {};
				const flexibleIds = new Set([
					DESCRIPTION_COLUMN_ID,
					lastVisibleColumnId,
				]);
				let usedWidth = 0;

				for (const colId of visibleColumnsInOrder) {
					if (flexibleIds.has(colId)) continue;

					const size =
						existingSizing[colId] ?? columnMinSizes[colId] ?? DEFAULT_MIN_SIZE;
					sizing[colId] = size;
					usedWidth += size;
				}

				const remainingWidth = width - usedWidth;
				const lastMin = columnMinSizes[lastVisibleColumnId] ?? DEFAULT_MIN_SIZE;
				const descriptionMin =
					columnMinSizes[DESCRIPTION_COLUMN_ID] ?? DEFAULT_MIN_SIZE;

				let descriptionWidth = clamp(
					remainingWidth - lastMin,
					descriptionMin,
					descriptionMaxSize,
				);
				let lastWidth = remainingWidth - descriptionWidth;

				if (lastWidth < lastMin) {
					lastWidth = Math.max(MIN_FLEX_COLUMN_WIDTH, lastMin);
					descriptionWidth = Math.max(
						MIN_FLEX_COLUMN_WIDTH,
						remainingWidth - lastWidth,
					);
				}

				sizing[DESCRIPTION_COLUMN_ID] = descriptionWidth;
				sizing[lastVisibleColumnId] = Math.max(
					MIN_FLEX_COLUMN_WIDTH,
					lastWidth,
				);

				return sizing;
			}

			const soleFlexColumnId = lastVisibleColumnId;
			if (!soleFlexColumnId) {
				return existingSizing;
			}

			const sizing: ColumnSizingState = {};
			let usedWidth = 0;

			for (const colId of visibleColumnsInOrder) {
				if (colId === soleFlexColumnId) continue;

				const size =
					existingSizing[colId] ?? columnMinSizes[colId] ?? DEFAULT_MIN_SIZE;
				sizing[colId] = size;
				usedWidth += size;
			}

			const remainingWidth = width - usedWidth;
			const minFlexSize = columnMinSizes[soleFlexColumnId] ?? DEFAULT_MIN_SIZE;

			if (soleFlexColumnId === DESCRIPTION_COLUMN_ID) {
				sizing[soleFlexColumnId] = clamp(
					remainingWidth,
					Math.max(MIN_FLEX_COLUMN_WIDTH, minFlexSize),
					descriptionMaxSize,
				);
			} else {
				sizing[soleFlexColumnId] = Math.max(
					MIN_FLEX_COLUMN_WIDTH,
					Math.max(minFlexSize, remainingWidth),
				);
			}

			return sizing;
		},
		[
			visibleColumnsInOrder,
			columnMinSizes,
			hasCappedDescriptionWithLastFlex,
			lastVisibleColumnId,
			descriptionMaxSize,
		],
	);

	// Set up ResizeObserver - re-run when columns change to catch late-mounting containers
	// biome-ignore lint/correctness/useExhaustiveDependencies: columns.length triggers re-run when data arrives
	useEffect(() => {
		// Skip measurement when disabled
		if (!enabled) return;

		let resizeObserver: ResizeObserver | null = null;
		let attempts = 0;
		let rafId: number | null = null;

		const setupObserver = () => {
			const element = containerRef.current;
			if (!element) {
				// Element not ready, keep trying for up to 2 seconds
				attempts++;
				if (attempts < 120) {
					rafId = requestAnimationFrame(setupObserver);
				}
				return;
			}

			// Measure immediately
			const width = element.clientWidth;
			if (width > 0) {
				setContainerWidth(width);
			}

			// Set up ResizeObserver for ongoing measurements
			resizeObserver = new ResizeObserver((entries) => {
				const entry = entries[0];
				if (entry) {
					const w = entry.contentRect.width;
					if (w > 0) {
						setContainerWidth(w);
					}
				}
			});
			resizeObserver.observe(element);
		};

		setupObserver();

		return () => {
			if (rafId) cancelAnimationFrame(rafId);
			if (resizeObserver) resizeObserver.disconnect();
		};
	}, [containerRef, columns.length, enabled]);

	const layoutKey = `${flexColumnId ?? "none"}:${lastVisibleColumnId ?? "none"}:${hasCappedDescriptionWithLastFlex}`;

	// Calculate sizes when container width changes or visible columns change
	useEffect(() => {
		// Skip calculation when disabled
		if (!enabled) return;

		if (containerWidth <= 0 || visibleColumnsInOrder.length === 0) {
			return;
		}

		const oldLayoutKey = previousLayoutKey.current;
		const layoutChanged = oldLayoutKey != null && oldLayoutKey !== layoutKey;

		setColumnSizing((prev) => {
			let baseSizing = prev;

			// Reset previously flexed columns when layout mode changes
			if (layoutChanged && oldLayoutKey) {
				const [oldFlexColumnId, oldLastColumnId] = oldLayoutKey.split(":");
				const resetIds = new Set(
					[oldFlexColumnId, oldLastColumnId].filter(
						(id) => id && id !== "none",
					),
				);

				for (const id of resetIds) {
					if (baseSizing[id]) {
						baseSizing = {
							...baseSizing,
							[id]: columnMinSizes[id] ?? DEFAULT_MIN_SIZE,
						};
					}
				}
			}

			return calculateColumnSizes(containerWidth, baseSizing);
		});

		previousLayoutKey.current = layoutKey;

		setIsInitialized(true);
	}, [
		containerWidth,
		visibleColumnsInOrder,
		calculateColumnSizes,
		enabled,
		layoutKey,
		columnMinSizes,
	]);

	// Handle manual column resizing
	const handleColumnSizingChange = useCallback(
		(
			updater:
				| ColumnSizingState
				| ((prev: ColumnSizingState) => ColumnSizingState),
		) => {
			setColumnSizing((old) => {
				const next = typeof updater === "function" ? updater(old) : updater;

				// Find which column was resized
				let resizedId: string | null = null;
				let maxDelta = 0;
				for (const id of Object.keys(next)) {
					const prevSize = old[id] ?? 0;
					const newSize = next[id] ?? 0;
					const delta = newSize - prevSize;
					if (Math.abs(delta) > Math.abs(maxDelta)) {
						maxDelta = delta;
						resizedId = id;
					}
				}

				if (maxDelta === 0) {
					return old;
				}

				// Last column resize is applied directly (it owns trailing spare space)
				if (
					resizedId === lastVisibleColumnId &&
					hasCappedDescriptionWithLastFlex
				) {
					return next;
				}

				const width = containerRef.current?.clientWidth ?? 0;
				if (width <= 0) {
					return calculateColumnSizes(width, next);
				}

				// Description manual resize: honor drag within cap, refill last column
				if (
					resizedId === DESCRIPTION_COLUMN_ID &&
					hasCappedDescriptionWithLastFlex &&
					lastVisibleColumnId
				) {
					const descriptionMin =
						columnMinSizes[DESCRIPTION_COLUMN_ID] ?? DEFAULT_MIN_SIZE;
					const descriptionWidth = clamp(
						next[DESCRIPTION_COLUMN_ID] ?? descriptionMin,
						descriptionMin,
						descriptionMaxSize,
					);

					let usedWidth = descriptionWidth;
					for (const colId of visibleColumnsInOrder) {
						if (
							colId === DESCRIPTION_COLUMN_ID ||
							colId === lastVisibleColumnId
						) {
							continue;
						}
						usedWidth +=
							next[colId] ?? columnMinSizes[colId] ?? DEFAULT_MIN_SIZE;
					}

					const lastMin =
						columnMinSizes[lastVisibleColumnId] ?? DEFAULT_MIN_SIZE;
					const lastWidth = Math.max(
						MIN_FLEX_COLUMN_WIDTH,
						Math.max(lastMin, width - usedWidth),
					);

					return {
						...next,
						[DESCRIPTION_COLUMN_ID]: descriptionWidth,
						[lastVisibleColumnId]: lastWidth,
					};
				}

				return calculateColumnSizes(width, next);
			});
		},
		[
			lastVisibleColumnId,
			hasCappedDescriptionWithLastFlex,
			calculateColumnSizes,
			containerRef,
			visibleColumnsInOrder,
			columnMinSizes,
			descriptionMaxSize,
		],
	);

	const resetColumnSizing = useCallback(() => {
		const width = containerRef.current?.clientWidth ?? containerWidth;
		if (width <= 0) {
			setColumnSizing({});
			return;
		}
		setColumnSizing(calculateColumnSizes(width, {}));
	}, [calculateColumnSizes, containerRef, containerWidth]);

	// Return disabled state if not enabled
	if (!enabled) {
		return disabledReturn;
	}

	return {
		columnSizing,
		isInitialized,
		handleColumnSizingChange,
		resetColumnSizing,
		lastVisibleColumnId,
		flexColumnId,
	};
}
